import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, Scale, ChevronDown, X } from 'lucide-react';
import api from '../lib/api';
import LegalReferencePopup from './LegalReferencePopup';

const OffenseCombobox = ({ value, onChange, placeholder, className = '' }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!search || search.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await api.get('/offenses', { params: { search } });
        setResults(resp.data?.slice(0, 15) || []);
        setIsOpen(true);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (offense) => {
    onChange(offense);
    setSearch('');
    setIsOpen(false);
  };

  const CATEGORY_COLORS = {
    crime: 'bg-red-100 text-red-800',
    delit: 'bg-orange-100 text-orange-800',
    contravention: 'bg-yellow-100 text-yellow-800',
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {value ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30" data-testid="selected-offense">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{value.name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{value.reference}</p>
          </div>
          <Badge variant="outline" className={`text-[10px] shrink-0 ${CATEGORY_COLORS[value.category] || ''}`}>
            {value.category}
          </Badge>
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => onChange(null)}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <div className="flex gap-1">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder || "Tapez pour rechercher une infraction..."}
              className="pl-8 pr-8 text-sm"
              onFocus={() => search.length >= 2 && setIsOpen(true)}
              data-testid="offense-search-input"
            />
            {search && (
              <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => { setSearch(''); setResults([]); }}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
          <Button variant="outline" size="icon" className="shrink-0" onClick={() => setShowPopup(true)} title="Parcourir la bibliotheque" data-testid="browse-offenses-btn">
            <Scale className="w-4 h-4" />
          </Button>
        </div>
      )}

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="offense-dropdown">
          {results.map((o, i) => (
            <div
              key={o.offense_id || i}
              className="px-3 py-2 hover:bg-muted/60 cursor-pointer border-b last:border-b-0 transition-colors"
              onClick={() => handleSelect(o)}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm flex-1">{o.name}</span>
                <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLORS[o.category] || ''}`}>
                  {o.category}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground truncate">{o.penalty}</p>
            </div>
          ))}
        </div>
      )}

      {isOpen && search.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-lg p-3 text-center text-sm text-muted-foreground">
          Aucune infraction trouvee pour &quot;{search}&quot;
        </div>
      )}

      <LegalReferencePopup open={showPopup} onClose={() => setShowPopup(false)} onSelect={handleSelect} mode="select" />
    </div>
  );
};

export default OffenseCombobox;
